import { useEffect, useRef, useState } from "react";
import type { ChangeEvent } from "react";
import Modal from "@modalSrc/Modal";
import "@style/Admin.scss";
import "@/style/sass/_languageValuesEditModal.scss";
import RightTimeApi from "@/API";
import { toast } from "react-toastify";
import { translateFromEnglishAsync } from "@/utils/translationService";
import {
  buildLanguageValueRows,
  canTranslateLanguageValueRow,
  isLanguageValueRowDirty,
  type LanguageValueExisting,
  type LanguageValueRow,
} from "@/utils/languageValuesEdit";
import ContentVersionHistoryButton from "@modalSrc/ContentVersionHistoryButton";
import ContentVersionHistorySelect from "@modalSrc/ContentVersionHistorySelect";
import {
  findContentVersionById,
  getCurrentContentVersionId,
  isViewingHistoricalContentVersion,
  mergeContentVersionNote,
  type ContentLanguageVersion,
} from "@/utils/contentVersionHistory";
import "@/style/sass/_contentVersionHistorySelect.scss";
import { getLanguageId, type LanguageLike } from "@/utils/languageUtils";

export type LanguageValuesEditMode = "Field" | "SectionControl" | "Company";

type SeedLanguage = {
  languageId: number;
  content?: string;
  fieldTranslationId?: number | null;
  sectionControlLanguageId?: number | null;
  companyContentLanguageId?: number | null;
  humanEdited?: boolean;
  languageName?: string;
};

type CompanyContentMeta = {
  id: number;
  companyId: number | null;
  name: string;
  contentTypeId: number;
};

type LanguageValuesEditModalProps = {
  isOpen: boolean;
  mode: LanguageValuesEditMode;
  onClose: (saved?: boolean) => void;
  contextLabel: string;
  fieldTranslationKey?: string | null;
  englishText?: string;
  sectionControlId?: number | null;
  companyId?: number | null;
  companyContentId?: number | null;
  initialLanguages?: SeedLanguage[];
  /** Optional override; when omitted the modal loads `/Translator/Languages` on open. */
  editableLanguages?: LanguageLike[];
  canEditEnglish?: boolean;
};

const ENGLISH_LANGUAGE: LanguageLike = {
  id: 1,
  name: "English",
  abbrev: "eng",
  ietf: "en-US",
};

function normalizeEditableLanguages(raw: unknown): LanguageLike[] {
  if (!Array.isArray(raw)) return [];

  return raw
    .map((item) => {
      if (!item || typeof item !== "object") return null;
      const record = item as Record<string, unknown>;
      const id = getLanguageId(record as LanguageLike);
      if (id <= 0) return null;
      return {
        id,
        name: String(record.name ?? record.Name ?? record.display ?? record.Display ?? ""),
        display: (record.display ?? record.Display) as string | undefined,
        abbrev: (record.abbrev ?? record.Abbrev) as string | undefined,
        ietf: (record.ietf ?? record.Ietf ?? record.IETF) as string | undefined,
      } satisfies LanguageLike;
    })
    .filter(Boolean) as LanguageLike[];
}

function withEnglishLanguage(languages: LanguageLike[]): LanguageLike[] {
  const foreign = languages.filter((lang) => getLanguageId(lang) > 1);
  const english = languages.find((lang) => getLanguageId(lang) === 1) ?? ENGLISH_LANGUAGE;
  return [english, ...foreign];
}

function toExisting(entries: SeedLanguage[]): LanguageValueExisting[] {
  return entries.map((entry) => ({
    languageId: Number(entry.languageId),
    content: String(entry.content ?? ""),
    fieldTranslationId: entry.fieldTranslationId ?? null,
    sectionControlLanguageId: entry.sectionControlLanguageId ?? null,
    companyContentLanguageId: entry.companyContentLanguageId ?? null,
    humanEdited: Boolean(entry.humanEdited),
  }));
}

export default function LanguageValuesEditModal({
  isOpen,
  mode,
  onClose,
  contextLabel,
  fieldTranslationKey,
  englishText,
  sectionControlId,
  companyId,
  companyContentId,
  initialLanguages,
  editableLanguages,
  canEditEnglish = true,
}: LanguageValuesEditModalProps) {
  const [rows, setRows] = useState<LanguageValueRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [translatingLanguageIds, setTranslatingLanguageIds] = useState<Record<number, boolean>>({});
  const [versionsByLanguageId, setVersionsByLanguageId] = useState<Record<number, ContentLanguageVersion[]>>({});
  const [selectedVersionIdByLanguageId, setSelectedVersionIdByLanguageId] = useState<Record<number, number | null>>({});
  const companyMetaRef = useRef<CompanyContentMeta | null>(null);

  useEffect(() => {
    if (!isOpen) {
      companyMetaRef.current = null;
      setVersionsByLanguageId({});
      setSelectedVersionIdByLanguageId({});
      return;
    }

    let cancelled = false;
    const seedLanguages = initialLanguages;
    const seedEnglishText = englishText;
    const seedSectionControlId = sectionControlId;
    const seedCompanyId = companyId;
    const seedCompanyContentId = companyContentId;
    const seedEditableLanguages = editableLanguages;
    const seedContextLabel = contextLabel;

    async function loadAsync() {
      setIsLoading(true);
      companyMetaRef.current = null;
      try {
        let languages = withEnglishLanguage(seedEditableLanguages ?? []);
        if (languages.filter((lang) => getLanguageId(lang) > 1).length === 0) {
          const languagesResp = await RightTimeApi.getAsync("/Translator/Languages");
          if (languagesResp?.ok) {
            languages = withEnglishLanguage(normalizeEditableLanguages(languagesResp.jsonData));
          }
        }

        const languageOptions = languages
          .map((lang) => ({
            id: getLanguageId(lang),
            name: String(lang.name ?? lang.display ?? ""),
            abbrev: lang.abbrev,
            ietf: lang.ietf,
          }))
          .filter((lang) => lang.id > 0);

        let existing = toExisting(seedLanguages ?? []);

        if (mode === "Field") {
          const resolvedEnglish = String(
            seedEnglishText
              ?? existing.find((entry) => entry.languageId === 1)?.content
              ?? ""
          ).trim();
          const needsFetch = resolvedEnglish.length > 0
            && !existing.some((entry) => entry.languageId > 1 && Number(entry.fieldTranslationId ?? 0) > 0);
          if (needsFetch) {
            const resp = await RightTimeApi.getAsync("/FieldTranslations");
            const list = Array.isArray(resp?.jsonData) ? resp.jsonData : [];
            const matches = list.filter((item: { englishText?: string; EnglishText?: string }) =>
              String(item.englishText ?? item.EnglishText ?? "").localeCompare(resolvedEnglish, undefined, {
                sensitivity: "accent",
              }) === 0
            );
            if (matches.length > 0) {
              existing = matches.map((item: Record<string, unknown>) => ({
                languageId: Number(item.languageId ?? item.LanguageId ?? 0),
                content: String(item.translation ?? item.Translation ?? ""),
                fieldTranslationId: Number(item.id ?? item.Id ?? 0) || null,
                humanEdited: Boolean(item.humanEdited ?? item.HumanEdited),
              }));
              if (!existing.some((entry) => entry.languageId === 1)) {
                existing.unshift({ languageId: 1, content: resolvedEnglish, humanEdited: false });
              }
            } else if (!existing.some((entry) => entry.languageId === 1)) {
              existing = [{ languageId: 1, content: resolvedEnglish, humanEdited: false }, ...existing];
            }
          } else if (resolvedEnglish && !existing.some((entry) => entry.languageId === 1)) {
            existing = [{ languageId: 1, content: resolvedEnglish, humanEdited: false }, ...existing];
          }
        }

        if (mode === "SectionControl" && seedSectionControlId) {
          const hasIds = existing.some((entry) => entry.sectionControlLanguageId);
          if (!hasIds) {
            const resp = await RightTimeApi.getAsync("/SectionControls");
            const list = Array.isArray(resp?.jsonData) ? resp.jsonData : [];
            const matches = list.filter(
              (item: { sectionControlId?: number; SectionControlId?: number }) =>
                Number(item.sectionControlId ?? item.SectionControlId) === Number(seedSectionControlId)
            );
            if (matches.length > 0) {
              existing = matches.map((item: Record<string, unknown>) => ({
                languageId: Number(item.languageId ?? item.LanguageId ?? 0),
                content: String(item.content ?? item.Content ?? ""),
                sectionControlLanguageId: Number(item.id ?? item.Id ?? 0) || null,
                humanEdited: false,
              }));
            }
          }
        }

        if (mode === "Company" && seedCompanyContentId) {
          const cid = Number(seedCompanyId ?? 0);
          const contentId = Number(seedCompanyContentId);
          const resp = await RightTimeApi.getAsync(`/Company/${Math.max(cid, 0)}/Content/${contentId}`);
          if (resp?.ok && resp.jsonData) {
            const payload = resp.jsonData as Record<string, unknown>;
            const content = (payload.content ?? payload.Content ?? payload) as Record<string, unknown>;
            companyMetaRef.current = {
              id: Number(content.id ?? content.Id ?? contentId),
              companyId: (() => {
                const raw = content.companyId ?? content.CompanyId ?? (cid > 0 ? cid : null);
                const n = Number(raw);
                return Number.isFinite(n) && n > 0 ? n : null;
              })(),
              name: String(content.name ?? content.Name ?? seedContextLabel ?? ""),
              contentTypeId: Number(content.contentTypeId ?? content.ContentTypeId ?? 0),
            };

            const langRows = Array.isArray(content.languages)
              ? content.languages
              : Array.isArray(payload.languages)
                ? payload.languages
                : [];
            if (langRows.length > 0) {
              const parsedRows = (langRows as Record<string, unknown>[])
                .map((item) => ({
                  languageId: Number(item.languageId ?? item.LanguageId ?? 0),
                  content: String(item.content ?? item.Content ?? ""),
                  companyContentLanguageId:
                    Number(item.companyContentLanguageId ?? item.CompanyContentLanguageId ?? item.id ?? item.Id ?? 0)
                    || null,
                  humanEdited: false,
                  versions: Array.isArray(item.versions ?? item.Versions)
                    ? (item.versions ?? item.Versions) as ContentLanguageVersion[]
                    : [],
                }))
                .filter((entry) => entry.languageId > 0);
              existing = parsedRows.map((row) => ({
                languageId: row.languageId,
                content: row.content,
                companyContentLanguageId: row.companyContentLanguageId,
                humanEdited: row.humanEdited,
              }));
              const nextVersions: Record<number, ContentLanguageVersion[]> = {};
              const nextSelected: Record<number, number | null> = {};
              parsedRows.forEach((entry) => {
                nextVersions[entry.languageId] = entry.versions;
                nextSelected[entry.languageId] = getCurrentContentVersionId(entry.versions);
              });
              setVersionsByLanguageId(nextVersions);
              setSelectedVersionIdByLanguageId(nextSelected);
            }
          } else if ((seedLanguages?.length ?? 0) === 0) {
            toast.error("Failed to load company content.");
          }
        }

        if (cancelled) return;

        setRows(
          buildLanguageValueRows({
            languages: languageOptions,
            existing,
            isSystemAdmin: canEditEnglish,
            canEditEnglish,
          })
        );
      } catch (error) {
        console.error("Failed to load language values:", error);
        toast.error("Failed to load language values.");
        if (!cancelled) {
          setRows([]);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    void loadAsync();
    return () => {
      cancelled = true;
    };
    // Reload when the modal opens or the identity of the edited item changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isOpen,
    mode,
    englishText,
    sectionControlId,
    companyId,
    companyContentId,
    fieldTranslationKey,
    canEditEnglish,
    editableLanguages,
  ]);

  const englishValue = rows.find((row) => row.languageId === 1)?.value ?? "";
  const hasDirtyRows = rows.some(isLanguageValueRowDirty);
  const isViewingAnyHistoricalVersion = mode === "Company" && rows.some((row) => {
    const versions = versionsByLanguageId[row.languageId] ?? [];
    const selectedVersionId = selectedVersionIdByLanguageId[row.languageId] ?? getCurrentContentVersionId(versions);
    return isViewingHistoricalContentVersion(selectedVersionId, versions);
  });

  function updateRowValue(languageId: number, value: string) {
    setRows((prev) =>
      prev.map((row) => (row.languageId === languageId ? { ...row, value } : row))
    );
  }

  async function handleTranslateRowAsync(row: LanguageValueRow) {
    if (!canTranslateLanguageValueRow(row, englishValue)) {
      if (row.humanEdited) {
        toast.info("This translation was manually edited and cannot be auto-translated.");
      }
      return;
    }

    const language = { id: row.languageId, name: row.languageName };

    setTranslatingLanguageIds((prev) => ({ ...prev, [row.languageId]: true }));
    try {
      const translated = await translateFromEnglishAsync(englishValue, language);
      updateRowValue(row.languageId, translated);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to translate text.";
      toast.error(message);
    } finally {
      setTranslatingLanguageIds((prev) => ({ ...prev, [row.languageId]: false }));
    }
  }

  async function handleSaveAsync() {
    const dirtyRows = rows.filter(isLanguageValueRowDirty);
    if (dirtyRows.length === 0 || isSaving) return;

    setIsSaving(true);
    try {
      if (mode === "Field") {
        const currentEnglish = englishValue.trim();
        if (!currentEnglish) {
          toast.error("English text is required.");
          return;
        }

        for (const row of dirtyRows) {
          if (row.languageId === 1) continue;
          const payload = {
            id: Number(row.fieldTranslationId ?? 0),
            key: fieldTranslationKey ?? undefined,
            englishText: currentEnglish,
            languageId: row.languageId,
            translation: row.value.trim(),
          };
          const response = await RightTimeApi.sendAsync("/FieldTranslation", payload);
          if (!response?.ok) throw new Error("Field translation save failed");
        }

        const englishDirty = dirtyRows.some((row) => row.languageId === 1);
        if (englishDirty) {
          const foreignRows = rows.filter((row) => row.languageId > 1 && Number(row.fieldTranslationId ?? 0) > 0);
          for (const row of foreignRows) {
            const payload = {
              id: Number(row.fieldTranslationId),
              key: fieldTranslationKey ?? undefined,
              englishText: currentEnglish,
              languageId: row.languageId,
              translation: row.value.trim(),
            };
            const response = await RightTimeApi.sendAsync("/FieldTranslation", payload);
            if (!response?.ok) throw new Error("Field translation English update failed");
          }
        }

        toast.success("Field translation saved.");
      } else if (mode === "SectionControl") {
        for (const row of dirtyRows) {
          const sectionControlLanguageId = Number(row.sectionControlLanguageId ?? 0);
          if (!sectionControlLanguageId) {
            toast.error(`No provisioned slot for ${row.languageName}. Use Languages → provision first.`);
            return;
          }
          if (row.englishLocked) {
            toast.error("System admin permission is required to edit English section controls.");
            return;
          }
          const response = await RightTimeApi.putAsync("/SectionControlLanguage", {
            sectionControlLanguageId,
            content: row.value,
          });
          if (!response?.ok) throw new Error("Section control save failed");
        }
        toast.success("Section control saved.");
      } else if (mode === "Company") {
        const meta = companyMetaRef.current;
        if (!meta?.id || !meta.contentTypeId) {
          toast.error("Company content metadata is missing.");
          return;
        }

        const languagesArray = rows
          .map((row) => ({
            languageId: row.languageId,
            content: row.value,
            companyContentLanguageId: row.companyContentLanguageId ?? undefined,
          }))
          .filter((lang) => String(lang.content ?? "").trim().length > 0);

        const data = {
          id: meta.id,
          companyId: meta.companyId && meta.companyId > 0 ? meta.companyId : null,
          name: meta.name,
          contentTypeId: meta.contentTypeId,
          languages: languagesArray,
        };
        const response = await RightTimeApi.sendAsync("/CompanyContent", data);
        if (!response?.ok) throw new Error("Company content save failed");
        toast.success("Company content saved.");
      }

      onClose(true);
    } catch (error) {
      console.error("Failed to save language values:", error);
      toast.error("Failed to save translation.");
    } finally {
      setIsSaving(false);
    }
  }

  function handleClose() {
    onClose(false);
  }

  if (!isOpen) return null;

  const title =
    mode === "Field"
      ? "Edit Field Translation"
      : mode === "SectionControl"
        ? "Edit Section Control"
        : "Edit Company Content";

  const footer = (
    <>
      <div className="footerLeft" />
      <div className="footerRight">
        <button type="button" className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>
          Cancel
        </button>
        <button
          type="button"
          className={`btn btn-primary ${!hasDirtyRows || isSaving || isViewingAnyHistoricalVersion ? "disabled" : ""}`}
          onClick={() => void handleSaveAsync()}
          disabled={!hasDirtyRows || isSaving || isLoading || isViewingAnyHistoricalVersion}
        >
          {isSaving ? "Saving..." : "Save"}
        </button>
      </div>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      className="languageValuesEditModal"
      title={title}
      footer={footer}
    >
      {contextLabel ? <div className="languageValuesContext">{contextLabel}</div> : null}
      {isLoading ? (
        <div className="languageValuesLoading">Loading...</div>
      ) : (
        <table className="languageValuesEditTable">
          <thead>
            <tr>
              <th>Language</th>
              <th>Value</th>
              {mode === "Company" ? <th>History</th> : null}
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const isTranslating = !!translatingLanguageIds[row.languageId];
              const canTranslate = canTranslateLanguageValueRow(row, englishValue);
              const rowVersions = versionsByLanguageId[row.languageId] ?? [];
              const selectedVersionId =
                selectedVersionIdByLanguageId[row.languageId] ?? getCurrentContentVersionId(rowVersions);
              const viewingHistory = mode === "Company"
                && isViewingHistoricalContentVersion(selectedVersionId, rowVersions);
              const selectedVersion = findContentVersionById(rowVersions, selectedVersionId);
              const displayedValue = viewingHistory ? (selectedVersion?.content ?? "") : row.value;
              return (
                <tr key={row.languageId} className={viewingHistory ? "viewingHistory" : undefined}>
                  <td>{row.languageName}</td>
                  <td>
                    <textarea
                      rows={3}
                      value={displayedValue}
                      readOnly={viewingHistory}
                      disabled={isSaving || row.englishLocked || isTranslating || viewingHistory}
                      onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                        updateRowValue(row.languageId, e.target.value)
                      }
                    />
                    {row.humanEdited ? (
                      <span
                        className="languageValuesHumanEditedHint"
                        title="Manually edited; protected from auto-translate and language reset"
                      >
                        Human edited
                      </span>
                    ) : null}
                  </td>
                  {mode === "Company" ? (
                    <td>
                      <div className="languageValuesHistoryCell">
                        <ContentVersionHistorySelect
                          selectId={`languageValuesHistory-${row.languageId}`}
                          versions={rowVersions}
                          value={selectedVersionId}
                          onChange={(versionId) => {
                            setSelectedVersionIdByLanguageId((prev) => ({
                              ...prev,
                              [row.languageId]: versionId,
                            }));
                          }}
                          label={
                            <ContentVersionHistoryButton
                              versions={rowVersions}
                              historyType="company"
                              onVersionNoteUpdated={(updated) => {
                                setVersionsByLanguageId((prev) => ({
                                  ...prev,
                                  [row.languageId]: mergeContentVersionNote(prev[row.languageId] ?? [], updated),
                                }));
                              }}
                            />
                          }
                        />
                      </div>
                    </td>
                  ) : null}
                  <td className="languageValuesRowActions">
                    {row.languageId > 1 ? (
                      <button
                        type="button"
                        className="btn btn-secondary"
                        disabled={viewingHistory || !canTranslate || isSaving || isTranslating}
                        onClick={() => void handleTranslateRowAsync(row)}
                        title={
                          row.humanEdited
                            ? "Manually edited translations cannot be auto-translated"
                            : isTranslating
                              ? "Translating..."
                              : "Translate English into this language"
                        }
                      >
                        {isTranslating ? "Translating..." : "Translate"}
                      </button>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </Modal>
  );
}
